import { logger } from '@zep/core-logger';

import type { ValidationResult } from './types';

/**
 * 다운로드 파일 확장자 차단 — 본문 추출 대상이 아니므로 사전에 거절.
 * 경로 어느 segment에서 끝나든 매칭한다 (`/foo.pdf/index.html` 같은 우회 차단).
 */
const FILE_EXTENSION_IN_PATH =
  /\/[^/]+\.(pdf|docx|xlsx|pptx|zip|rar|exe|dmg)(?:\/|$)/i;

/**
 * 한글(자/모/완성형) 포함 여부 — DOC §4: 한국어 검색어가 URL 자리에 들어온
 * 케이스를 가능한 한 빠르게 INVALID_URL로 분기한다. `new URL()`이 IDN을
 * punycode로 받아주긴 하지만, 사용자가 "네이버"처럼 호스트가 아닌 검색어를
 * 입력한 경우는 명백한 input mistake이므로 별도 친절한 분기로 잡는다.
 */
const CONTAINS_KOREAN = /[ㄱ-ㅎㅏ-ㅣ가-힣]/;

/**
 * 내부 공백 — DOC §4: trim 후에도 공백이 남아있으면 단일 URL이 아니라
 * "구글 검색" 같은 검색어 입력이다.
 */
const CONTAINS_WHITESPACE = /\s/;

/**
 * 입력에 명시 스킴(scheme)이 있으면 https://로 재작성하지 않는다.
 *
 * 두 케이스를 한 번에 잡는다:
 *  - `ftp://example.com` 같은 `://` 형 — `://`가 있으면 명백히 스킴.
 *  - `javascript:alert(1)`, `data:text/html,...`처럼 `://`가 없는 스킴 —
 *    이때는 첫 `:`가 첫 `.` 보다 앞에 있는지로 판별한다 (`example.com:8080`
 *    같은 host:port와 구분하기 위함).
 *
 * 이렇게 분기하면 ftp/javascript/file/data 등이 https://로 silently 재작성돼
 * isValid:true로 빠지는 경로(P1)와, 도트 체크에 걸려 INVALID_URL로 잘못
 * 분류되는 경로(P2)가 모두 차단된다.
 */
const hasExplicitScheme = (input: string): boolean => {
  if (input.includes('://')) return true;
  const colonIdx = input.indexOf(':');
  if (colonIdx === -1) return false;
  const dotIdx = input.indexOf('.');
  return dotIdx === -1 || colonIdx < dotIdx;
};

const tryParseUrl = (input: string): URL | null => {
  try {
    return new URL(input);
  } catch {
    return null;
  }
};

const classifyParsed = (parsed: URL): ValidationResult => {
  if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
    return { isValid: false, error: 'UNUSABLE_URL' };
  }
  if (FILE_EXTENSION_IN_PATH.test(parsed.pathname)) {
    return { isValid: false, error: 'FILE_URL' };
  }
  return { isValid: true, normalizedUrl: parsed.toString() };
};

/**
 * Website URL 검증 — 외부 호출용. 실패 시 어떤 입력이 거절됐는지 funnel
 * 분석을 위해 `ai_website_validation_failed`를 한 번만 logging한다. 검증
 * 자체는 `runValidation`이 담당해 단위 테스트가 logger를 의식하지 않도록 한다.
 */
export function validateWebsiteUrl(url: string): ValidationResult {
  const result = runValidation(url);
  if (!result.isValid) {
    const safeInput = url ?? '';
    logger.track('ai_website_validation_failed', {
      error_code: result.error,
      input_length: safeInput.length,
      // 긴 URL/페이지 경로 PII 노출을 줄이기 위해 앞 32자만 — host + 스킴
      // 식별에는 충분하다.
      input_prefix: safeInput.slice(0, 32),
    });
  }
  return result;
}

/**
 * 검증 항목 (순서대로 실행, 실패 즉시 단축):
 * 1. 빈 문자열·공백뿐 → INVALID_URL
 * 2. 한글/내부 공백 포함 → INVALID_URL (DOC §4 — 검색어 오입력)
 * 3. 명시 스킴이 있는 입력은 그대로 파싱 → http/https면 통과 분기로,
 *    그 외(`ftp://`·`javascript:`·`file:`·`data:` 등)는 UNUSABLE_URL.
 * 4. 스킴 없는 입력에 `.` 없음 → INVALID_URL
 *    ("abc"·"localhost"가 https://abc/로 통과되는 것 방지)
 * 5. https:// 자동 보정 후 파싱 실패 → INVALID_URL
 * 6. 파일 다운로드 확장자 → FILE_URL
 * 7. 그 외 → 통과 (정규화된 URL 반환)
 */
function runValidation(url: string): ValidationResult {
  if (!url || url.trim() === '') {
    return { isValid: false, error: 'INVALID_URL' };
  }

  const trimmed = url.trim();

  if (CONTAINS_KOREAN.test(trimmed) || CONTAINS_WHITESPACE.test(trimmed)) {
    return { isValid: false, error: 'INVALID_URL' };
  }

  if (hasExplicitScheme(trimmed)) {
    const parsed = tryParseUrl(trimmed);
    return parsed
      ? classifyParsed(parsed)
      : { isValid: false, error: 'INVALID_URL' };
  }

  if (!trimmed.includes('.')) {
    return { isValid: false, error: 'INVALID_URL' };
  }

  const parsed = tryParseUrl(`https://${trimmed}`);
  return parsed
    ? classifyParsed(parsed)
    : { isValid: false, error: 'INVALID_URL' };
}
